import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import Header from '../components/Header';
import HeroBanner from '../components/HeroBanner';
import ContentRow, { FeatureBanner } from '../components/ContentRow';
import ContentModal from '../components/ContentModal';
import Footer from '../components/Footer';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function HomePage() {
  const [featured, setFeatured] = useState([]);
  const [categories, setCategories] = useState({});
  const [allContents, setAllContents] = useState([]);
  const [continueWatching, setContinueWatching] = useState([]);
  const [recRows, setRecRows] = useState([]);
  const [highlights, setHighlights] = useState([]);
  const [highlightDuration, setHighlightDuration] = useState(8);
  const [topMovies, setTopMovies] = useState([]);
  const [topSeries, setTopSeries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalContentId, setModalContentId] = useState(null);
  const [modalSource, setModalSource] = useState(null);
  const [launchTransition, setLaunchTransition] = useState(false);
  const launchTarget = useRef(null);
  const navigate = useNavigate();
  const { activeProfile, profileVersion } = useAuth();
  const profileId = activeProfile?.id || '';

  const fetchData = useCallback(async () => {
    try {
      const [featuredRes, catRes, allRes, top10Res] = await Promise.all([
        axios.get(`${API}/contents/featured`, { withCredentials: true }),
        axios.get(`${API}/contents/categories`, { withCredentials: true }),
        axios.get(`${API}/contents`, { withCredentials: true }),
        axios.get(`${API}/top10`, { params: ['Tout public', '7+', '10+'].includes(activeProfile?.max_age_rating) ? { kids: true } : {}, withCredentials: true })
      ]);
      setFeatured(featuredRes.data);
      setCategories(catRes.data);
      setAllContents(allRes.data);
      setTopMovies(top10Res.data.movies || []);
      setTopSeries(top10Res.data.series || []);
      // Fetch continue-watching (profile-scoped)
      try {
        const { data } = await axios.get(`${API}/continue-watching`, { params: { profile_id: profileId }, withCredentials: true });
        setContinueWatching(data);
      } catch {}
      // Personalized rows "Parce que vous avez regardé X"
      try {
        const { data } = await axios.get(`${API}/recommendations`, { params: { profile_id: profileId }, withCredentials: true });
        setRecRows(Array.isArray(data) ? data : []);
      } catch {}
      // Fetch highlights and config
      try {
        const [hlRes, cfgRes] = await Promise.all([
          axios.get(`${API}/contents/highlights`, { withCredentials: true }),
          axios.get(`${API}/banner-config`, { withCredentials: true }),
        ]);
        setHighlights(hlRes.data);
        if (cfgRes.data.highlight_scroll_duration) setHighlightDuration(cfgRes.data.highlight_scroll_duration);
      } catch {}
    } catch (err) {
      console.error('Error fetching data:', err);
    } finally {
      setLoading(false);
    }
  }, [profileId]);

  useEffect(() => { fetchData(); }, [fetchData, profileVersion]);

  // Multi-device sync: refresh "Reprendre la lecture" when the tab regains focus/visibility
  useEffect(() => {
    const refresh = () => {
      if (document.visibilityState !== 'visible' || !profileId) return;
      axios.get(`${API}/continue-watching`, { params: { profile_id: profileId }, withCredentials: true })
        .then(r => setContinueWatching(r.data)).catch(() => {});
    };
    document.addEventListener('visibilitychange', refresh);
    window.addEventListener('focus', refresh);
    return () => { document.removeEventListener('visibilitychange', refresh); window.removeEventListener('focus', refresh); };
  }, [profileId]);

  const openModal = useCallback((id, rect) => { setModalContentId(id); setModalSource(rect || null); }, []);

  const handleLaunchMedia = useCallback((contentId) => {
    // Rule: launching the main video stops any playing trailer (sound + video)
    document.querySelectorAll('video').forEach(v => { v.pause(); v.muted = true; });
    launchTarget.current = contentId;
    setLaunchTransition(true);
    setTimeout(() => {
      navigate(`/watch/${contentId}`);
    }, 2000);
  }, [navigate]);

  // Listen for modal open events from within ContentModal (similar titles click)
  useEffect(() => {
    const handler = (e) => setModalContentId(e.detail);
    window.addEventListener('openContentModal', handler);
    return () => window.removeEventListener('openContentModal', handler);
  }, []);

  // Listen for launch events from HeroBanner
  useEffect(() => {
    const handler = (e) => handleLaunchMedia(e.detail);
    window.addEventListener('launchMedia', handler);
    return () => window.removeEventListener('launchMedia', handler);
  }, [handleLaunchMedia]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: 'linear-gradient(180deg, #11041F 0%, #382C4E 100%)' }}>
        <div className="w-10 h-10 border-[3px] border-[#E9641A]/30 border-t-[#E9641A] rounded-full animate-spin" />
      </div>
    );
  }

  // Build sections with highlight banners
  const KID_RATINGS = ['Tout public', '7+', '10+'];
  const isKid = KID_RATINGS.includes(activeProfile?.max_age_rating);
  const kidAllow = c => c && (c.type === 'jeunesse' || c.type === 'game' || (c.categories || []).includes('jeunesse') || c.category === 'jeunesse');
  const sections = [];
  let hlIdx = 0;
  const insertBanner = () => {
    if (highlights.length > 0 && hlIdx < highlights.length && highlights[hlIdx]) {
      sections.push({ type: 'banner', key: `banner-${hlIdx}`, item: highlights[hlIdx] });
      hlIdx++;
    }
  };

  // "Reprendre la lecture" always first
  if (continueWatching.length > 0) sections.push({ type: 'continue', key: 'continue-watching', title: 'Reprendre la lecture', items: continueWatching });

  // "Parce que vous avez regardé X"
  recRows.forEach((r, i) => {
    const items = isKid ? (r.items || []).filter(kidAllow) : (r.items || []);
    if (items.length >= 3) sections.push({ type: 'row', key: `rec-${i}`, title: `Parce que vous avez regardé ${r.based_on_title}`, items });
  });

  // Fixed order: Nouveautés → Jeux → Top 10 Films → Top 10 Séries → Série → Fiction → Documentaire → Jeunesse → Originals
  const fixedOrder = ['nouveautes', 'jeux'];
  const afterTop10 = ['series', 'fiction', 'documentaire', 'jeunesse', 'originals'];
  const catOrder = categories._order || [];
  const catMap = {};
  for (const cat of catOrder) catMap[cat.id] = cat;

  // Nouveautés + Jeux
  for (const catId of fixedOrder) {
    const cat = catMap[catId];
    const items = categories[catId];
    if (!cat || !items || items.length === 0) continue;
    sections.push({ type: 'row', key: cat.id, title: cat.name, items });
    if (sections.filter(s => s.type === 'row').length % 2 === 0) insertBanner();
  }

  // Top 10 Films
  if (topMovies.length > 0) {
    sections.push({ type: 'top10', key: 'top10-films', title: "Top 10 des films aujourd'hui", items: topMovies });
  }

  // Top 10 Séries
  if (topSeries.length > 0) {
    sections.push({ type: 'top10', key: 'top10-series', title: "Top 10 des séries aujourd'hui", items: topSeries });
  }

  insertBanner();

  // Série → Fiction → Documentaire → Jeunesse → Originals
  for (const catId of afterTop10) {
    const cat = catMap[catId];
    const items = categories[catId];
    if (!cat || !items || items.length === 0) continue;
    sections.push({ type: 'row', key: cat.id, title: cat.name, items });
    if (sections.filter(s => s.type === 'row').length % 2 === 0) insertBanner();
  }

  // Any remaining categories not in the fixed lists
  const handled = new Set([...fixedOrder, ...afterTop10]);
  for (const cat of catOrder) {
    if (handled.has(cat.id)) continue;
    const items = categories[cat.id];
    if (!items || items.length === 0) continue;
    sections.push({ type: 'row', key: cat.id, title: cat.name, items });
  }

  // Kid profile: restricted catalog (Jeunesse, Jeux, Séries jeunesse, Originals Maya+ jeunesse)
  let finalSections = sections;
  if (isKid) {
    finalSections = [];
    const cwKid = continueWatching.filter(kidAllow);
    if (cwKid.length) finalSections.push({ type: 'continue', key: 'continue-watching', title: 'Reprendre la lecture', items: cwKid });
    recRows.forEach((r, i) => {
      const items = (r.items || []).filter(kidAllow);
      if (items.length >= 3) finalSections.push({ type: 'row', key: `rec-${i}`, title: `Parce que vous avez regardé ${r.based_on_title}`, items });
    });
    const jeunesse = categories['jeunesse'] || [];
    const jeux = categories['jeux'] || [];
    const sj = jeunesse.filter(c => c.type === 'series');
    const oj = (categories['originals'] || []).filter(kidAllow);
    if (jeunesse.length) finalSections.push({ type: 'row', key: 'jeunesse', title: 'Jeunesse', items: jeunesse });
    if (jeux.length) finalSections.push({ type: 'row', key: 'jeux', title: 'Jeux', items: jeux });
    if (topMovies.length) finalSections.push({ type: 'top10', key: 'top10-films', title: "Top 10 des films aujourd'hui", items: topMovies });
    if (topSeries.length) finalSections.push({ type: 'top10', key: 'top10-series', title: "Top 10 des séries aujourd'hui", items: topSeries });
    if (sj.length) finalSections.push({ type: 'row', key: 'series-jeunesse', title: 'Séries jeunesse', items: sj });
    if (oj.length) finalSections.push({ type: 'row', key: 'originals-jeunesse', title: 'Originals Maya+ jeunesse', items: oj });
  }
  const heroItems = isKid ? featured.filter(kidAllow) : featured;

  return (
    <div className="min-h-screen relative" style={{ background: 'linear-gradient(180deg, #11041F 0%, #1a0e2e 50%, #382C4E 100%)' }} data-testid="home-page">
      <Header />
      <HeroBanner items={heroItems} />

      {/* More space between hero and first row */}
      <div className="relative z-10 mt-10 pb-20 space-y-8 px-4 md:px-12">
        {finalSections.map(section => {
          if (section.type === 'banner') {
            return <FeatureBanner key={section.key} item={section.item} onOpenModal={openModal} />;
          }
          if (section.type === 'top10') {
            return <ContentRow key={section.key} title={section.title} items={section.items} isTop10 onOpenModal={openModal} launching={launchTransition} onLaunch={handleLaunchMedia} />;
          }
          if (section.type === 'continue') {
            return <ContentRow key={section.key} title={section.title} items={section.items} showProgress onOpenModal={openModal} launching={launchTransition} onLaunch={handleLaunchMedia} />;
          }
          return <ContentRow key={section.key} title={section.title} items={section.items} onOpenModal={openModal} launching={launchTransition} onLaunch={handleLaunchMedia} />;
        })}
      </div>

      <Footer />

      {/* Fade-to-black overlay */}
      <div
        className="fixed inset-0 bg-black pointer-events-none z-[90] ease-out"
        style={{
          opacity: launchTransition ? 1 : 0,
          transitionProperty: 'opacity',
          transitionDuration: '1800ms',
          transitionDelay: launchTransition ? '300ms' : '0ms',
        }}
      />

      {/* Content Modal */}
      {modalContentId && (
        <ContentModal contentId={modalContentId} onClose={() => { setModalContentId(null); setModalSource(null); }} sourceRect={modalSource} />
      )}
    </div>
  );
}
